import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState, useCallback, useEffect } from "react";
import { SlidersHorizontal, FileText } from "lucide-react";
import type { SearchableSelectOption } from "../components/ui/SearchableSelect/SearchableSelect";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Table } from "../components/ui/Table/Table";
import { Button } from "../components/ui/Button/Button";
import { Input } from "../components/ui/Input/Input";
import { Select } from "../components/ui/Select/Select";
import { FormField } from "../components/ui/FormField/FormField";
import { Modal } from "../components/ui/Modal/Modal";
import { SearchableSelect } from "../components/ui/SearchableSelect/SearchableSelect";
import { StatusBadge } from "../components/ui/StatusBadge/StatusBadge";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { EmptyState } from "../components/ui/EmptyState/EmptyState";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { FilterBar } from "../components/ui/FilterBar/FilterBar";
import { useActivityList, useCreateActivity, useUpdateActivity } from "../hooks/use-activities";
import { ACTIVITY_TYPES, ACTIVITY_DIRECTIONS, ACTIVITY_OUTCOMES } from "@crm/shared/schemas";
import type { ActivityType, ActivityDirection, ActivityOutcome } from "@crm/shared/types";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { formatDate } from "../lib/date";
import styles from "./Activities.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/activities",
	component: withPermission(ActivitiesPage, "activities", "read"),
	validateSearch: (search: Record<string, unknown>) => ({
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
		type: String(search.type || ""),
		search: String(search.search || ""),
		createdAtFrom: String(search.createdAtFrom || ""),
		createdAtTo: String(search.createdAtTo || ""),
		companyId: search.companyId ? String(search.companyId) : undefined,
		personId: search.personId ? String(search.personId) : undefined,
		dueDateFrom: String(search.dueDateFrom || ""),
		dueDateTo: String(search.dueDateTo || ""),
		isCompleted: String(search.isCompleted || ""),
		ownerId: String(search.ownerId || ""),
		direction: String(search.direction || ""),
		outcome: String(search.outcome || ""),
	}),
});

function ActivitiesPage() {
	const { t } = useTranslation();
	const navigate = useNavigate({ from: "/activities" });
	const urlSearch = route.useSearch();
	const {
		page,
		size,
		type: typeFilter,
		companyId,
		personId,
		search: searchQuery,
		createdAtFrom,
		createdAtTo,
		dueDateFrom,
		dueDateTo,
		isCompleted,
		ownerId,
		direction: directionFilter,
		outcome: outcomeFilter,
	} = urlSearch;
	const usersQuery = useQuery({
		queryKey: ["activity-owners"],
		queryFn: () =>
			api.get<{
				items: Array<{ id: string; firstName: string; lastName: string }>;
				canSeeAll: boolean;
			}>("/api/activities/owners"),
		staleTime: 5 * 60 * 1000,
	});
	const isManager = (usersQuery.data?.items.length ?? 0) > 0;

	const setPage = (p: number) => navigate({ search: (prev) => ({ ...prev, page: p }) });
	const setPageSize = (s: number) =>
		navigate({ search: (prev) => ({ ...prev, size: s, page: 1 }) });
	const [createOpen, setCreateOpen] = useState(false);
	const [filterModalOpen, setFilterModalOpen] = useState(false);
	const [form, setForm] = useState({
		type: "call" as const,
		subject: "",
		content: "",
		dueDate: "",
		companyId: companyId ?? "",
		personId: personId ?? "",
		contactName: "",
		contactOrgName: "",
		contactPhone: "",
		contactEmail: "",
		location: "",
		direction: "" as ActivityDirection | "",
		outcome: "" as ActivityOutcome | "",
	});
	const [formError, setFormError] = useState("");
	const [newPersonOpen, setNewPersonOpen] = useState(false);
	const [newPerson, setNewPerson] = useState({
		firstName: "",
		lastName: "",
		title: "",
		phone: "",
		email: "",
	});
	const [newPersonError, setNewPersonError] = useState("");
	const [newPersonSaving, setNewPersonSaving] = useState(false);
	const [personsRefresh, setPersonsRefresh] = useState(0);
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [initialOptions, setInitialOptions] = useState<Record<string, SearchableSelectOption>>({});

	useEffect(() => {
		const opts: Record<string, SearchableSelectOption> = {};
		const fetches: Promise<void>[] = [];
		if (companyId) {
			fetches.push(
				api
					.get<{ id: string; legalName: string }>(`/api/companies/${companyId}`)
					.then((c) => {
						opts.company = { value: c.id, label: c.legalName };
					})
					.catch(() => {}),
			);
		}
		if (personId) {
			fetches.push(
				api
					.get<{ id: string; firstName: string; lastName: string }>(`/api/persons/${personId}`)
					.then((p) => {
						opts.person = { value: p.id, label: `${p.firstName} ${p.lastName}` };
					})
					.catch(() => {}),
			);
		}
		Promise.all(fetches).then(() => {
			setInitialOptions(opts);
			if (companyId || personId) {
				setCreateOpen(true);
			}
		});
	}, [companyId, personId]);
	const { data, isLoading, isError, refetch } = useActivityList({
		type: typeFilter || undefined,
		search: searchQuery || undefined,
		createdAtFrom: createdAtFrom || undefined,
		createdAtTo: createdAtTo || undefined,
		dueDateFrom: dueDateFrom || undefined,
		dueDateTo: dueDateTo || undefined,
		isCompleted: isCompleted === "" ? undefined : isCompleted === "true",
		ownerId: isManager && ownerId ? ownerId : undefined,
		direction: directionFilter || undefined,
		outcome: outcomeFilter || undefined,
		page,
		limit: size,
	});
	const createActivity = useCreateActivity();

	const activities = data?.items ?? [];
	const total = data?.total ?? 0;

	const typeOptions = ACTIVITY_TYPES.map((v) => ({ value: v, label: t(v) }));
	const directionOptions = ACTIVITY_DIRECTIONS.map((v) => ({ value: v, label: t(v) }));
	const outcomeOptions = ACTIVITY_OUTCOMES.map((v) => ({ value: v, label: t(v) }));

	const loadCompanies = useCallback(async (query: string) => {
		const res = await api.get<{ items: Array<{ id: string; legalName: string }> }>(
			`/api/companies?limit=50${query ? `&search=${encodeURIComponent(query)}` : ""}`,
		);
		return res.items.map((c) => ({ value: c.id, label: c.legalName }));
	}, []);

	const loadPersons = useCallback(
		async (query: string) => {
			if (!form.companyId) return [];
			const res = await api.get<{
				items: Array<{ id: string; firstName: string; lastName: string }>;
			}>(
				`/api/persons?limit=50&companyId=${form.companyId}${query ? `&search=${encodeURIComponent(query)}` : ""}`,
			);
			return res.items.map((p) => ({ value: p.id, label: `${p.firstName} ${p.lastName}` }));
		},
		[form.companyId],
	);

	const saveNewPerson = async () => {
		setNewPersonError("");
		if (!newPerson.firstName.trim() || !newPerson.lastName.trim()) {
			setNewPersonError(t("requiredField"));
			return;
		}
		setNewPersonSaving(true);
		try {
			const created = await api.post<{ id: string; firstName: string; lastName: string }>(
				"/api/persons",
				{ ...newPerson, companyId: form.companyId },
			);
			const label = `${created.firstName} ${created.lastName}`;
			setInitialOptions((prev) => ({ ...prev, person: { value: created.id, label } }));
			setForm((prev) => ({ ...prev, personId: created.id }));
			setPersonsRefresh((n) => n + 1);
			setNewPerson({ firstName: "", lastName: "", title: "", phone: "", email: "" });
			setNewPersonOpen(false);
		} catch (err) {
			setNewPersonError(err instanceof Error ? err.message : t("createFailed"));
		} finally {
			setNewPersonSaving(false);
		}
	};

	const submit = () => {
		setFormError("");
		const errors: Record<string, string> = {};
		if (!form.type) errors.type = t("requiredField");
		if (!form.subject?.trim()) errors.subject = t("requiredField");
		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) return;
		createActivity.mutate(
			{
				...form,
				dueDate: form.dueDate || undefined,
				companyId: form.companyId || undefined,
				personId: form.personId || undefined,
				contactName: form.contactName || undefined,
				contactOrgName: form.contactOrgName || undefined,
				contactPhone: form.contactPhone || undefined,
				contactEmail: form.contactEmail || undefined,
				location: form.location || undefined,
				direction: form.direction || undefined,
				outcome: form.outcome || undefined,
			},
			{
				onSuccess: () => {
					setCreateOpen(false);
					setForm({
						type: "call",
						subject: "",
						content: "",
						dueDate: "",
						companyId: "",
						personId: "",
						contactName: "",
						contactOrgName: "",
						contactPhone: "",
						contactEmail: "",
						location: "",
						direction: "",
						outcome: "",
					});
					setFormError("");
					alert(t("createSuccess"));
				},
				onError: (err) => {
					setFormError(err instanceof Error ? err.message : t("createFailed"));
				},
			},
		);
	};

	const filterDefs = [
		{
			key: "search",
			type: "text" as const,
			label: t("search"),
			placeholder: t("searchActivityPlaceholder"),
			width: 240,
		},
		{
			key: "type",
			type: "select" as const,
			label: t("type"),
			options: typeOptions,
		},
		{ key: "direction", type: "select" as const, label: t("direction"), options: directionOptions },
		{
			key: "isCompleted",
			type: "select" as const,
			label: t("status"),
			options: [
				{ value: "false", label: t("pending") },
				{ value: "true", label: t("completed") },
			],
		},
		{ key: "outcome", type: "select" as const, label: t("outcomeResult"), options: outcomeOptions },
		...(isManager
			? [
					{
						key: "ownerId",
						type: "select" as const,
						label: t("owner"),
						options: (usersQuery.data?.items ?? []).map((u) => ({
							value: u.id,
							label: `${u.firstName} ${u.lastName}`,
						})),
					},
				]
			: []),
		{ key: "dueDate", type: "dateRange" as const, label: t("filterDueDate") },
		{ key: "createdAt", type: "dateRange" as const, label: t("filterCreatedAt") },
	];
	const filterValues = {
		search: searchQuery,
		createdAtFrom,
		createdAtTo,
		dueDateFrom,
		dueDateTo,
		type: typeFilter,
		isCompleted,
		ownerId,
		direction: directionFilter,
		outcome: outcomeFilter,
	};
	const handleFilterChange = (vals: Record<string, string>) => {
		navigate({
			search: (prev) => ({
				...prev,
				search: vals.search || "",
				createdAtFrom: vals.createdAtFrom || "",
				createdAtTo: vals.createdAtTo || "",
				dueDateFrom: vals.dueDateFrom || "",
				dueDateTo: vals.dueDateTo || "",
				type: vals.type || "",
				isCompleted: vals.isCompleted || "",
				ownerId: vals.ownerId || "",
				direction: vals.direction || "",
				outcome: vals.outcome || "",
				page: 1,
			}),
		});
	};
	const activeFilterCount = [
		typeFilter,
		directionFilter,
		isCompleted,
		outcomeFilter,
		ownerId,
		dueDateFrom,
		dueDateTo,
		createdAtFrom,
		createdAtTo,
	].filter(Boolean).length;

	return (
		<div>
			<PageHeader
				title={t("activities")}
				action={<Button onClick={() => setCreateOpen(true)}>{t("createActivity")}</Button>}
			/>

			<div className={[styles.filters, styles.filterBarDesktop].filter(Boolean).join(" ")}>
				<FilterBar filters={filterDefs} values={filterValues} onChange={handleFilterChange} />
			</div>

			<div className={styles.filterBarMobile ?? ""}>
				<Input
					placeholder={t("searchActivityPlaceholder")}
					value={searchQuery}
					onChange={(e) => handleFilterChange({ ...filterValues, search: e.target.value })}
					className={styles.mobileSearchInput ?? ""}
				/>
				<button
					type="button"
					className={styles.filterTrigger ?? ""}
					onClick={() => setFilterModalOpen(true)}
				>
					<SlidersHorizontal size={16} />
					{t("filter")}
					{activeFilterCount > 0 && (
						<span className={styles.filterCount ?? ""}>{activeFilterCount}</span>
					)}
				</button>
			</div>

			<Card bodyClassName={styles.listCardBody ?? ""}>
				{isLoading && <LoadingSpinner />}
				{isError && <ErrorFallback onRetry={refetch} />}
				{!isLoading && !isError && (
					<>
						<div className={styles.desktopTable ?? ""}>
							<Table
								className={styles.compactTable ?? ""}
								columns={[
									{
										key: "type",
										header: t("type"),
										render: (row) => <StatusBadge status={row.type} type="activity" />,
									},
									{ key: "subject", header: t("subject") },
									{
										key: "contact",
										header: t("contact"),
										render: (row) =>
											row.companyName ||
											row.personName ||
											row.contactOrgName ||
											row.contactName ||
											"-",
									},
									...(isManager
										? [
												{
													key: "ownerName",
													header: t("owner"),
													render: (row: (typeof activities)[number]) => row.ownerName || "-",
												},
											]
										: []),
									{
										key: "dueDate",
										header: t("dueDate"),
										render: (row) => (row.dueDate ? formatDate(row.dueDate) : "-"),
									},
									{
										key: "isCompleted",
										header: t("isCompleted"),
										render: (row) => (row.isCompleted ? t("yes") : t("no")),
									},
									{
										key: "actions",
										header: t("actions"),
										render: (row) => (
											<Link to={`/activities/${row.id}` as never} className={styles.link}>
												{t("viewDetails")}
											</Link>
										),
									},
								]}
								data={activities}
								keyExtractor={(row) => row.id}
							/>
						</div>

						<div className={styles.mobileList ?? ""}>
							{activities.length === 0 && <EmptyState />}
							{activities.map((row) => (
								<div key={row.id} className={styles.mobileCard}>
									<div className={styles.mobileCardTop}>
										<StatusBadge status={row.type} type="activity" />
										{row.dueDate && (
											<span className={styles.mobileCardDue}>{formatDate(row.dueDate)}</span>
										)}
									</div>
									<div className={styles.mobileCardName}>{row.subject}</div>
									<div className={styles.mobileCardSub}>
										{[
											row.companyName || row.personName || row.contactOrgName || row.contactName,
											isManager ? row.ownerName : null,
										]
											.filter(Boolean)
											.join(" · ")}
									</div>
									<div className={styles.mobileCardBottom}>
										<span className={styles.mobileCardStatus}>
											{row.isCompleted ? t("completed") : t("pending")}
										</span>
										<Link
											to={`/activities/${row.id}` as never}
											className={styles.mobileIconBtn}
											aria-label={t("viewDetails")}
										>
											<FileText size={15} />
										</Link>
									</div>
								</div>
							))}
						</div>
					</>
				)}
				<Pagination
					page={page}
					pageSize={size}
					total={total}
					onPageChange={setPage}
					onPageSizeChange={setPageSize}
				/>
			</Card>

			<Modal
				isOpen={filterModalOpen}
				onClose={() => setFilterModalOpen(false)}
				title={t("filter")}
			>
				<div className={styles.filterModalGrid ?? ""}>
					<FilterBar
						filters={filterDefs.filter((f) => f.key !== "search")}
						values={filterValues}
						onChange={handleFilterChange}
					/>
				</div>
				<div className={styles.modalActions}>
					<Button onClick={() => setFilterModalOpen(false)}>{t("close")}</Button>
				</div>
			</Modal>

			<Modal
				isOpen={createOpen}
				onClose={() => {
					setCreateOpen(false);
					navigate({
						to: ".",
						search: {
							page: 1,
							size: 20,
							type: "",
							search: "",
							createdAtFrom: "",
							createdAtTo: "",
							dueDateFrom: "",
							dueDateTo: "",
							isCompleted: "",
							ownerId: "",
							direction: "",
							outcome: "",
							companyId: undefined,
							personId: undefined,
						},
					});
				}}
				title={t("createActivity")}
			>
				{formError && <div className={styles.formError}>{formError}</div>}
				<FormField label={t("type")} required>
					<Select
						options={typeOptions}
						value={form.type}
						onChange={(e) => setForm({ ...form, type: e.target.value as never })}
						error={fieldErrors.type ?? ""}
					/>
				</FormField>
				<FormField label={t("direction")}>
					<Select
						options={[{ value: "", label: "-" }, ...directionOptions]}
						value={form.direction}
						onChange={(e) =>
							setForm({ ...form, direction: e.target.value as ActivityDirection | "" })
						}
					/>
				</FormField>
				<FormField label={t("company")}>
					<SearchableSelect
						value={form.companyId}
						onChange={(val) => {
							setForm({ ...form, companyId: val, personId: "" });
							setNewPersonOpen(false);
							setInitialOptions((prev) => {
								const next = { ...prev };
								delete next.person;
								return next;
							});
						}}
						loadOptions={loadCompanies}
						initialOption={initialOptions.company}
						placeholder={t("selectCompany")}
						clearable
					/>
				</FormField>
				<FormField label={t("contactPersons")}>
					<SearchableSelect
						value={form.personId}
						onChange={(val) => setForm({ ...form, personId: val })}
						loadOptions={loadPersons}
						refetchKey={`${form.companyId}:${personsRefresh}`}
						initialOption={initialOptions.person}
						placeholder={form.companyId ? t("selectPerson") : t("selectCompanyFirst")}
						clearable
						disabled={!form.companyId}
					/>
					{form.companyId && !newPersonOpen && (
						<button
							type="button"
							className={styles.inlineAction}
							onClick={() => setNewPersonOpen(true)}
						>
							+ {t("addNewPerson")}
						</button>
					)}
				</FormField>

				{form.companyId && newPersonOpen && (
					<div className={styles.inlineForm}>
						{newPersonError && <div className={styles.formError}>{newPersonError}</div>}
						<FormField label={t("firstName")} required>
							<Input
								value={newPerson.firstName}
								onChange={(e) => setNewPerson({ ...newPerson, firstName: e.target.value })}
							/>
						</FormField>
						<FormField label={t("lastName")} required>
							<Input
								value={newPerson.lastName}
								onChange={(e) => setNewPerson({ ...newPerson, lastName: e.target.value })}
							/>
						</FormField>
						<FormField label={t("jobTitle")}>
							<Input
								value={newPerson.title}
								onChange={(e) => setNewPerson({ ...newPerson, title: e.target.value })}
							/>
						</FormField>
						<FormField label={t("phone")}>
							<Input
								value={newPerson.phone}
								onChange={(e) => setNewPerson({ ...newPerson, phone: e.target.value })}
							/>
						</FormField>
						<FormField label={t("email")}>
							<Input
								value={newPerson.email}
								onChange={(e) => setNewPerson({ ...newPerson, email: e.target.value })}
							/>
						</FormField>
						<div className={styles.inlineFormActions}>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => {
									setNewPersonOpen(false);
									setNewPersonError("");
								}}
							>
								{t("cancel")}
							</Button>
							<Button size="sm" onClick={saveNewPerson} isLoading={newPersonSaving}>
								{t("save")}
							</Button>
						</div>
					</div>
				)}
				{!form.companyId && !form.personId && (
					<>
						<div className={styles.hint}>{t("noCustomerHint")}</div>
						<FormField label={t("contactOrgName")}>
							<Input
								value={form.contactOrgName}
								onChange={(e) => setForm({ ...form, contactOrgName: e.target.value })}
							/>
						</FormField>
						<FormField label={t("contactName")}>
							<Input
								value={form.contactName}
								onChange={(e) => setForm({ ...form, contactName: e.target.value })}
							/>
						</FormField>
						<FormField label={t("phone")}>
							<Input
								value={form.contactPhone}
								onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
							/>
						</FormField>
						<FormField label={t("email")}>
							<Input
								value={form.contactEmail}
								onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
							/>
						</FormField>
					</>
				)}
				<FormField label={t("subject")} required>
					<Input
						value={form.subject}
						onChange={(e) => setForm({ ...form, subject: e.target.value })}
						error={fieldErrors.subject}
					/>
				</FormField>
				<FormField label={t("content")}>
					<Input
						value={form.content}
						onChange={(e) => setForm({ ...form, content: e.target.value })}
					/>
				</FormField>
				<FormField label={t("location")}>
					<Input
						value={form.location}
						onChange={(e) => setForm({ ...form, location: e.target.value })}
					/>
				</FormField>
				<FormField label={t("outcomeResult")}>
					<Select
						options={[{ value: "", label: "-" }, ...outcomeOptions]}
						value={form.outcome}
						onChange={(e) => setForm({ ...form, outcome: e.target.value as ActivityOutcome | "" })}
					/>
				</FormField>
				<FormField label={t("dueDate")}>
					<Input
						type="date"
						value={form.dueDate}
						onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
					/>
				</FormField>
				<div className={styles.modalActions}>
					<Button variant="ghost" onClick={() => setCreateOpen(false)}>
						{t("cancel")}
					</Button>
					<Button onClick={submit} isLoading={createActivity.isPending}>
						{t("create")}
					</Button>
				</div>
			</Modal>
		</div>
	);
}
